import React from "react";
import { createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import { Helmet } from "react-helmet";
import Header from "../common/ToolsHeader";
import Settings from "./Settings";
import Tabs from "./Tabs";
import { connect, useSelector } from "react-redux";
import {
  getContestLineups,
  getContestPlayers,
  getContestStacks,
  stopLoading,
} from "./store/actions";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MobileCover from "../common/MobileCover";
import Authentication from "../common/Authentication";
import { SitesResponse, SlateResponse, SportsResponse } from "../common/store/types";
import { userSubscribed, useUserId } from "../utils/authentication";
import LoginRegisterContainer from "../common/LoginRegisterContainer";
import Subscribe from "../common/Subscribe";
import { IRootState } from "../../store/reducer";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: "100vh",
      backgroundColor: "#000A16",
      color: theme.palette.common.white,
      fontFamily: "Roboto",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    contentWrapper: {
      flexGrow: 1,
    },
  })
);

interface ReduxProps {
  readonly loading: boolean;
  readonly generatedSlate: SlateResponse;
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly poolSize: number;
}

interface DispatchProps {
  readonly StopLoading: () => void;
  readonly GetContestPlayers: (slateId: string, user: string) => void;
  readonly GetContestLineups: (slateId: string, user: string) => void;
  readonly GetContestStacks: (slateId: string, user: string) => void;
}

type Props = ReduxProps & DispatchProps;

const ContestGenerator = (props: Props) => {
  const classes = useStyles();
  const {
    loading,
    generatedSlate,
    selectedSite,
    selectedSport,
    poolSize,
    StopLoading,
    GetContestPlayers,
    GetContestLineups,
    GetContestStacks,
  } = props;
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const userId = useUserId();
  const authUserId = 'awe' + userId
  const { authenticated } = useSelector((state: IRootState) => state.common)
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);
  const [loadingPct, setLoadingPct] = React.useState("0%");
  const interval = React.useRef<ReturnType<typeof setInterval>>();
  const queryClient = new QueryClient();

  React.useEffect(() => {
    const statusCall = async () => {
      const progress = await fetch(
        `${baseUrl}/api/contests/getSimulationStatus?SlateId=${
          generatedSlate?.slateId || 0
        }&User=${authUserId}&StatusType=ContestGen`
      ).then((res) => res.json());

      if (progress.progress === 1) {
        StopLoading();
        GetContestPlayers(generatedSlate?.slateId?.toString(), authUserId);
        GetContestLineups(generatedSlate?.slateId?.toString(), authUserId);
        GetContestStacks(generatedSlate?.slateId?.toString(), authUserId);
      }
    };
    if (loading) {
      let totalMilliseconds = 0;
      let loadingTime = loadingTimeDict[`${generatedSlate.sport}-${poolSize}`];
      loadingTime = loadingTime * 1.15;
      if (generatedSlate.type?.toUpperCase() === "SHOWDOWN"){
        loadingTime = loadingTime / 2;
      }

      interval.current = setInterval(() => {
        if (loading && loadingTime > totalMilliseconds) {
          setLoadingPct(
            ((totalMilliseconds * 0.98 * 100) / loadingTime).toFixed(0)
            );
          }
          if (totalMilliseconds > 3000 && totalMilliseconds % 5000 === 4900) {
          statusCall();
        }
        totalMilliseconds += 10;
      }, 10);
      return () => {
        clearInterval(interval.current);
        setLoadingPct("0");
      };
    }
  }, [loading]);
  
  const loadingTimeDict: { [key: string]: number } = {
    "MLB-250": 30000,
    "MLB-500": 45000,
    "MLB-1000": 70000,
    "MLB-1500": 60000,
    "MLB-2000": 70000,
    "MLB-5000": 70000,
    "MLB-10000": 70000,
    "MLB-15000": 90000,
    "MLB-20000": 100000,
    "MLB-25000": 110000,
    "PGA-250": 30000,
    "PGA-500": 45000,
    "PGA-1000": 70000,
    "PGA-1500": 60000,
    "PGA-2000": 70000,
    "PGA-5000": 70000,
    "PGA-10000": 70000,
    "PGA-15000": 90000,
    "PGA-20000": 100000,
    "PGA-25000": 110000,
    "NBA-250": 30000,
    "NBA-500": 45000,
    "NBA-1000": 70000,
    "NBA-1500": 60000,
    "NBA-2000": 70000,
    "NBA-5000": 70000,
    "NBA-10000": 70000,
    "NBA-15000": 90000,
    "NBA-20000": 100000,
    "NBA-25000": 110000,
    "NHL-250": 30000,
    "NHL-500": 45000,
    "NHL-1000": 70000,
    "NHL-1500": 60000,
    "NHL-2000": 70000,
    "NHL-5000": 70000,
    "NHL-10000": 70000,
    "NHL-15000": 90000,
    "NHL-20000": 100000,
    "NHL-25000": 110000,
    "NFL-250": 60000,
    "NFL-500": 60000,
    "NFL-1000": 60000,
    "NFL-1500": 60000,
    "NFL-2000": 60000,
    "NFL-5000": 60000,
    "NFL-10000": 60000,
    "NFL-15000": 90000,
    "NFL-20000": 100000,
    "NFL-25000": 120000,
    "NFL-50000": 120000,
    "MMA-250": 40000,
    "MMA-500": 40000,
    "MMA-1000": 40000,
    "MMA-1500": 40000,
    "MMA-2000": 40000,
    "MMA-5000": 40000,
    "MMA-10000": 40000,
    "MMA-15000": 60000,
    "MMA-20000": 70000,
    "MMA-25000": 40000,
    "MMA-50000": 40000,
    "NASCAR-250": 40000,
    "NASCAR-500": 40000,
    "NASCAR-1000": 40000,
    "NASCAR-1500": 40000,
    "NASCAR-2000": 40000,
    "NASCAR-5000": 40000,
    "NASCAR-10000": 40000,
    "NASCAR-15000": 60000,
    "NASCAR-20000": 70000,
    "NASCAR-25000": 80000,
    "CFB-250": 60000,
    "CFB-500": 60000,
    "CFB-1000": 60000,
    "CFB-1500": 60000,
    "CFB-2000": 60000,
    "CFB-5000": 60000,
    "CFB-10000": 60000,
    "CFB-15000": 90000,
    "CFB-20000": 100000,
    "CFB-25000": 110000,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Authentication app='CONTESTGEN'>
      <Grid>
        <Helmet>
          <title>Contest Generator</title>
          <meta name="description" content="Stokastic Contest Generator" />
        </Helmet>
        <MobileCover>
          <Grid className={classes.root}>
            <Grid item xs={12}>
              <Header />
            </Grid>
            {
              userId ? (
                <Grid
                  container
                  item
                  xs={12}
                  direction="column"
                  className={classes.contentWrapper}
                  >
                  <Grid item xs={12}>
                    <Settings />
                  </Grid>
                  {
                    isUserSubscribed ? (
                      <Grid container item xs={12} style={{ flexGrow: 1, padding: 0 }}>
                        <Tabs loadingPercent={loadingPct} />
                      </Grid>

                    ) : <Subscribe toolName='Contest Generator' />
                  }
                </Grid>
              ) : <LoginRegisterContainer toolName='Contest Generator' />
            }
          </Grid>
        </MobileCover>
      </Grid>
      </Authentication>
    </QueryClientProvider>

  );
};

const mapStateToProps = (state: any) => {
  const { loading, generatedSlate, poolSize } = state.contestGenerator;
  const { selectedSport, selectedSite } = state.common;

  return {
    loading,
    generatedSlate,
    poolSize,
    selectedSport,
    selectedSite,
  };
};

const mapDispatchToProps = {
  StopLoading: stopLoading,
  GetContestPlayers: getContestPlayers,
  GetContestLineups: getContestLineups,
  GetContestStacks: getContestStacks,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContestGenerator);
